/**
 * Compare user tasting answers against a GrapeMinds wine detail.
 *
 * Status per step:
 * - match: close enough → green
 * - mismatch: differs greatly → red
 * - null: no reliable reference → uncolored
 */

import { formatAnswerForDisplay } from "./wineTastingGrid";

const SCALE_MM = ["Lav", "Medium minus", "Medium", "Medium plus", "Høj"];
const SCALE_BODY = ["Let", "Medium minus", "Medium", "Medium plus", "Fuld"];
const SCALE_LENGTH = ["Kort", "Medium minus", "Medium", "Medium plus", "Lang"];
const SCALE_SWEETNESS = [
  "Knastør",
  "Tør",
  "Tør med let restsødme",
  "Halvtør",
  "Medium-sød",
  "Sød",
  "Meget sød",
];

const COUNTRY_ALIASES = {
  it: ["italy", "italien", "italia", "it"],
  fr: ["france", "frankrig", "francia", "fr"],
  es: ["spain", "spanien", "españa", "espana", "es"],
  de: ["germany", "tyskland", "deutschland", "de"],
  us: ["usa", "united states", "america", "us", "united states of america"],
  pt: ["portugal", "pt"],
  at: ["austria", "østrig", "osterreich", "österreich", "at"],
  au: ["australia", "australien", "au"],
  nz: ["new zealand", "new zealand", "nz"],
  cl: ["chile", "cl"],
  ar: ["argentina", "argentinien", "ar"],
  za: ["south africa", "sydafrika", "za"],
};

function normText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokens(value) {
  return normText(value)
    .split(" ")
    .filter((t) => t.length > 2);
}

/** Map GrapeMinds flavor_profile 1–10 onto a 5-band scale. */
export function scoreToBandIndex(score, bandCount = 5) {
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  const clamped = Math.min(10, Math.max(1, n));
  return Math.min(bandCount - 1, Math.floor((clamped - 1) / (10 / bandCount)));
}

function indexOnScale(value, scale) {
  const raw = formatAnswerForDisplay(value);
  if (!raw) return null;
  const idx = scale.findIndex((s) => s.toLowerCase() === raw.toLowerCase());
  return idx >= 0 ? idx : null;
}

function compareScale(userValue, expectedIndex, scale) {
  if (expectedIndex == null) return null;
  const userIndex = indexOnScale(userValue, scale);
  if (userIndex == null) return null;
  const delta = Math.abs(userIndex - expectedIndex);
  if (delta <= 1) return "match";
  if (delta >= 2) return "mismatch";
  return "match";
}

function textOverlap(userValue, expectedValue) {
  const user = normText(userValue);
  const expected = normText(expectedValue);
  if (!user || !expected) return null;
  if (user === expected) return "match";
  if (user.includes(expected) || expected.includes(user)) return "match";
  const u = new Set(tokens(user));
  const e = tokens(expected);
  if (!e.length || !u.size) return null;
  const hits = e.filter((t) => u.has(t)).length;
  if (hits >= 1) return "match";
  return "mismatch";
}

function compareCountry(userValue, countryCode) {
  const user = normText(userValue);
  const code = String(countryCode || "").toLowerCase();
  if (!user || !code) return null;
  const aliases = COUNTRY_ALIASES[code] || [code];
  if (aliases.some((a) => user === a || user.includes(a) || a.includes(user))) return "match";
  if (user === code) return "match";
  return "mismatch";
}

function compareColor(userValue, gmColor) {
  const user = normText(userValue);
  const color = normText(gmColor);
  if (!user || !color) return null;
  if (color === "red" && (user === "red" || user === "rod")) return "match";
  if (color === "white" && (user === "white" || user === "hvid")) return "match";
  if ((color === "rose" || color === "rosé") && (user.includes("rose") || user.includes("rosa"))) {
    return "match";
  }
  if (user === "red" || user === "rod" || user === "white" || user === "hvid") return "mismatch";
  return null;
}

function sweetnessFromResidual(residual) {
  const r = normText(residual);
  if (!r) return null;
  if (/(bone\s*dry|trocken|secco|dry|brut)/.test(r) && !/off|demi|medium|sweet|moelleux|doux/.test(r)) {
    if (/bone/.test(r) || /trocken/.test(r)) return 0;
    return 1; // Dry
  }
  if (/off[\s-]*dry|halbtrocken|demi[\s-]*sec/.test(r)) return 3;
  if (/medium[\s-]*sweet|moelleux|lieblich/.test(r)) return 4;
  if (/luscious|dessert|sweet|doux|aszu|sgn|vt/.test(r)) return 5;
  if (/slight|rs\b/.test(r)) return 2;
  return null;
}

function compareGrape(userValue, grapeList) {
  const user = normText(userValue);
  if (!user || !grapeList?.length) return null;
  const expectedJoined = grapeList.map(normText).filter(Boolean);
  if (expectedJoined.some((g) => user.includes(g) || g.includes(user))) return "match";
  const userTokens = new Set(tokens(user));
  for (const g of expectedJoined) {
    for (const t of tokens(g)) {
      if (userTokens.has(t)) return "match";
    }
  }
  return "mismatch";
}

function compareVintage(userValue, expectedVintage) {
  const user = String(userValue || "").match(/\b(?:19|20)\d{2}\b/);
  const expected = String(expectedVintage || "").match(/\b(?:19|20)\d{2}\b/);
  if (!user || !expected) return null;
  const u = Number(user[0]);
  const e = Number(expected[0]);
  const delta = Math.abs(u - e);
  if (delta === 0) return "match";
  if (delta <= 1) return "match";
  if (delta >= 3) return "mismatch";
  return "match";
}

/**
 * Build expected answer labels (for tooltips / display) from GrapeMinds detail.
 */
export function expectedAnswersFromWine(detail) {
  if (!detail) return {};
  const fp = detail.flavor_profile || {};
  const expected = {};

  if (detail.color === "red" || detail.color === "white") {
    expected.sight_colour_type = detail.color === "red" ? "Rød" : "Hvid";
  }

  const acidIdx = scoreToBandIndex(fp.acidity);
  if (acidIdx != null) expected.structure_acidity = SCALE_MM[acidIdx];

  const tanninIdx = scoreToBandIndex(fp.tannins);
  if (tanninIdx != null) expected.structure_tannin_volume = SCALE_MM[tanninIdx];

  const alcoholIdx = scoreToBandIndex(fp.alcohol);
  if (alcoholIdx != null) expected.structure_alcohol = SCALE_MM[alcoholIdx];

  const bodyIdx = scoreToBandIndex(fp.body);
  if (bodyIdx != null) expected.structure_body = SCALE_BODY[bodyIdx];

  const finishIdx = scoreToBandIndex(fp.finish);
  if (finishIdx != null) expected.palate_length = SCALE_LENGTH[finishIdx];

  const sweetFromProfile = scoreToBandIndex(fp.sweetness, SCALE_SWEETNESS.length);
  const sweetFromResidual = sweetnessFromResidual(detail.residual_sugar);
  const sweetIdx = sweetFromResidual != null ? sweetFromResidual : sweetFromProfile;
  if (sweetIdx != null) expected.structure_sweetness = SCALE_SWEETNESS[sweetIdx];

  if (detail.grapes?.length) expected.final_grape = detail.grapes.join(", ");
  else if (detail.grape) expected.final_grape = detail.grape;

  if (detail.country) expected.final_country = detail.country;
  if (detail.region_name || detail.region) {
    expected.final_appellation = detail.region_name || detail.region;
  }
  if (detail.producer) expected.final_producer = detail.producer;
  if (detail.vintage) expected.final_vintage = detail.vintage;

  // Soft hints for initial conclusion
  if (detail.country) expected.initial_countries = detail.country;
  if (detail.region_name) expected.initial_regions = detail.region_name;
  if (detail.grapes?.length) expected.initial_grape = detail.grapes.join(", ");

  return expected;
}

/**
 * @returns {Record<string, "match"|"mismatch">}
 */
export function compareTastingAnswers(userAnswers, detail) {
  const expected = expectedAnswersFromWine(detail);
  const out = {};

  const put = (id, status) => {
    if (status === "match" || status === "mismatch") out[id] = status;
  };

  put("sight_colour_type", compareColor(userAnswers.sight_colour_type, detail?.color));

  put(
    "structure_acidity",
    compareScale(userAnswers.structure_acidity, scoreToBandIndex(detail?.flavor_profile?.acidity), SCALE_MM)
  );
  put(
    "structure_tannin_volume",
    compareScale(
      userAnswers.structure_tannin_volume,
      scoreToBandIndex(detail?.flavor_profile?.tannins),
      SCALE_MM
    )
  );
  put(
    "structure_alcohol",
    compareScale(userAnswers.structure_alcohol, scoreToBandIndex(detail?.flavor_profile?.alcohol), SCALE_MM)
  );
  put(
    "structure_body",
    compareScale(userAnswers.structure_body, scoreToBandIndex(detail?.flavor_profile?.body), SCALE_BODY)
  );
  put(
    "palate_length",
    compareScale(userAnswers.palate_length, scoreToBandIndex(detail?.flavor_profile?.finish), SCALE_LENGTH)
  );

  const sweetIdx =
    sweetnessFromResidual(detail?.residual_sugar) ??
    scoreToBandIndex(detail?.flavor_profile?.sweetness, SCALE_SWEETNESS.length);
  put("structure_sweetness", compareScale(userAnswers.structure_sweetness, sweetIdx, SCALE_SWEETNESS));

  put(
    "final_grape",
    compareGrape(userAnswers.final_grape, detail?.grapes?.length ? detail.grapes : [detail?.grape].filter(Boolean))
  );
  put("final_country", compareCountry(userAnswers.final_country, detail?.country));
  put(
    "final_appellation",
    textOverlap(userAnswers.final_appellation, detail?.region_name || detail?.region)
  );
  put("final_producer", textOverlap(userAnswers.final_producer, detail?.producer));
  put("final_vintage", compareVintage(userAnswers.final_vintage, detail?.vintage));

  put("initial_countries", compareCountry(userAnswers.initial_countries, detail?.country));
  put(
    "initial_regions",
    textOverlap(userAnswers.initial_regions, detail?.region_name || detail?.region)
  );
  put(
    "initial_grape",
    compareGrape(userAnswers.initial_grape, detail?.grapes?.length ? detail.grapes : [detail?.grape].filter(Boolean))
  );

  // Attach expected labels for UI tooltips
  out.__expected = expected;
  return out;
}

export function comparisonCellSx(status, fallbackBg) {
  if (status === "match") {
    return { bgcolor: "rgba(46, 125, 50, 0.18)" };
  }
  if (status === "mismatch") {
    return { bgcolor: "rgba(198, 40, 40, 0.16)" };
  }
  return { bgcolor: fallbackBg };
}
